// Core
export { B3d, b3d, showB3dStats } from './tosi-b3d.js'
/*
Re-export the Babylon namespace the library itself is using.

Babylon is a peer dependency, so a consumer importing '@babylonjs/core'
separately can end up with a SECOND copy — two Vector3 classes that fail
`instanceof` against each other. Taking it from here is the guarantee that you
have the same one. It is also what live doc examples should import: there is no
`BABYLON` global (a long-standing assumption in ~16 of them, and one line away
from a ReferenceError in every case).
*/
export * as BABYLON from '@babylonjs/core'
export type {
  SceneAdditions,
  SceneAdditionHandler,
  DebugPanelSource,
  FrameInfo,
} from './tosi-b3d.js'

// Device-capability probe (measure-don't-guess quality budgets)
export { B3dProbe, b3dProbe, runProbe } from './b3d-probe.js'
export {
  getPerfProfile,
  setPerfProfile,
  setQuality,
  getQuality,
  effectiveTier,
  qualityBudgets,
  resolveBudget,
  headsetDevice,
  onQualityChange,
} from './b3d-quality.js'
export type { QualitySetting, NumericBudgetKey } from './b3d-quality.js'
export {
  PROBE_VERSION,
  classify,
  budgetsForTier,
  resolveProfile,
  defaultProfile,
  isStandaloneHmd,
  tierCap,
} from './perf-probe.js'
export type {
  PerfTier,
  PerfMeasurements,
  PerfBudgets,
  PerfProfile,
  StoredProfile,
  ClassHints,
} from './perf-probe.js'

// World simulation (narrative-driver boundary)
export { WorldStore } from './world-store.js'
export { WorldView, defaultMeshFactory } from './world-view.js'
export type { MeshFactory } from './world-view.js'
export {
  proximityRung,
  rungNominal,
  routePortals,
  containmentPath,
} from './world-topology.js'
export { runMinSimConformance } from './min-sim-conformance.js'
export type {
  ConformanceHarness,
  TickableMinSim,
} from './min-sim-conformance.js'
export type {
  WorldState,
  WorldEntity,
  EntityComponents,
  HealthComponent,
  InventoryEntry,
  FactionComponent,
  InteractableComponent,
  EntityId,
  EntityKind,
  EntityIntent,
  Behavior,
  Zone,
  ZoneId,
  SpawnSpec,
  SimulationEvent,
  EventHandler,
  Unsubscribe,
  WorldApi,
  // Contract A — the coordinate-free surface (minimum-sim.md §8; additive, retires flat at B-SIM-1)
  PlaceId,
  PortalId,
  ChoiceId,
  Shape,
  Proximity,
  PlaceKind,
  Place,
  Portal,
  PlacedEntity,
  Anchor,
  SteerTarget,
  Choice,
  SchematicView,
  MinSimApi,
} from './world-contract.js'

// Utilities
export {
  findB3dOwner,
  B3dChild,
  AbstractMesh,
  buildAxes,
  isOff,
  actualMeshes,
  enterXR,
  applyMaterialConventions,
  materialConventions,
  publicName,
  isIgnored,
  placeOnSurface,
  boundingBottomOffset,
  hierarchyExtents,
  sceneDelta,
  sceneFrame,
  // Documented API that was reachable only by a deep import — which `exports`
  // (a bare string) blocks with ERR_PACKAGE_PATH_NOT_EXPORTED. `isNoCollide`'s
  // shipped JSDoc instructs consumers to call it, and CLAUDE.md tells them to
  // use `semanticParent`; instructing someone to call something they cannot
  // import is worse than not documenting it.
  markUiMesh,
  markCollisionGroup,
  collisionGroups,
  inCollisionGroup,
  isNoCollide,
  collidable,
  pickWorld,
  rayProxies,
  type RayProxy,
  cameraIsAttached,
  semanticParent,
  refuseOwnAncestor,
  refuseOwnAncestors,
  conventionName,
  simHalted,
  controlsLive,
} from './b3d-utils.js'
export type { XRStuff, XRParams, SimGateOwner } from './b3d-utils.js'

// The pure flight model, so a consumer can predict what the aircraft will do
// without instantiating one — `equilibriumSpeed` is what the HUD's set-point
// mark is drawn from, and a mission planner wants the same number.
export {
  regime,
  flyByWireStep,
  targetVelocity,
  chaseVelocity,
  equilibriumSpeed,
} from './fly-by-wire.js'
export type {
  FlyByWireConfig,
  FlyByWireCommand,
  FlyByWireState,
} from './fly-by-wire.js'

/*
The MEDIUM primitive as a namespace, not bare names: `plane`, `sphere` and
`crossing` are common nouns and would collide the moment anything else wants
them (`carve` already has a sphere). Types stay top-level.
*/
import * as mediumNs from './medium.js'
export const medium = mediumNs
export type {
  Medium,
  PlaneMedium,
  SphereMedium,
  MediumVec3,
  MediumCrossing,
} from './medium.js'

/*
See-through portal math, namespaced: `sideOf` and `crossedPortal` are generic
enough to collide, and `portalCamera` reads better qualified.
*/
import * as portalNs from './portal-transform.js'
export const portalTransform = portalNs

// Logical asset URLs (retarget the host in one place; see asset-url.ts)
export { setAssetBase, getAssetBase, assetUrl } from './asset-url.js'

// Pure spatial-attachment transform math (see SPATIAL-DESIGN.md)
export {
  add,
  sub,
  quatConjugate,
  quatMul,
  rotateVector,
  quatFromAxisAngle,
  composePose,
  relativePose,
  placeRelative,
  IDENTITY_QUAT,
} from './spatial-transform.js'
// Vec3 is already exported (surface-sampler) and is structurally identical.
export type { Quat, Pose } from './spatial-transform.js'

// Pure aircraft-HUD math (radar-trace projection + horizon + glass projection)
export {
  hudTrace,
  horizonTransform,
  glassUV,
  hudPointFromUV,
  lockFillOpacity,
  arcDashArray,
} from './hud-math.js'
export type { HudTrace, HudTraceOptions, HorizonTransform } from './hud-math.js'
// HUD driver (meters / horizon / radar traces over the HUD SVG)
export { createHudController, loadHud, buildFallbackHud } from './hud.js'
export type {
  HudController,
  HudControllerOptions,
  MeterName,
  TraceKind,
  HudTraceInput,
  Side,
  HudWarning,
} from './hud.js'
export { B3dHud, b3dHud } from './b3d-hud.js'

// Scene components
export { B3dLoader, b3dLoader } from './b3d-loader.js'
export { B3dLibrary, b3dLibrary } from './b3d-library.js'
export { B3dSun, b3dSun, cascadesFollowCamera } from './b3d-shadows.js'
export { B3dReflections, b3dReflections } from './b3d-reflections.js'
export { B3dSkybox, b3dSkybox } from './b3d-skybox.js'
export { B3dWater, b3dWater } from './b3d-water.js'
export { B3dLight, b3dLight } from './b3d-light.js'
export {
  B3dLamp,
  B3dPointLight,
  b3dPointLight,
  B3dSpotLight,
  b3dSpotLight,
  B3dAreaLight,
  b3dAreaLight,
} from './b3d-lamp.js'
// A lamp's whole life as ONE curve per channel, split into attack / sustain /
// decay by two markers — the model behind its flicker, pulse and fade.
export {
  segmentTimes,
  lightPreset,
  lightPresets,
  DEFAULT_PROGRAM,
  lightProgramSchema,
  canonicalProgram,
  validateProgram,
  sampleLight,
  programPosition,
  lightPhase,
  isAnimated,
  shiftHue,
  NO_MODULATION,
} from './light-modulation.js'
export type {
  LightPreset,
  LightProgram,
  LightPhase,
  ChannelCurves,
  ModulationCurve,
  ModulationSample,
} from './light-modulation.js'
/*
A province's climate layer as DATA — bipolar bias curves plus the two water
scalars. Pure (no DOM, no Babylon) so a consumer can validate and serialise one
headlessly; see PROVINCE-DESIGN.md -> "TWO editors".
*/
export {
  sampleClimate,
  composeClimate,
  applyClimate,
  provinceClimateSchema,
  canonicalClimate,
  validateClimate,
  DEFAULT_AMOUNTS,
  NO_CLIMATE,
} from './province-climate.js'
export type {
  ProvinceClimate,
  ClimateSample,
  ClimateCurve,
  ClimateAmounts,
} from './province-climate.js'
/*
JSON Schema for the scene primitives — no DOM, no Babylon, so a consumer's
headless runner can read them. Exists so nobody hand-copies our attributes:
ensemble's hand-written skybox schema carried 6 of 16 and disagreed on a
default (#63). Drift is caught by a test, not promised.
*/
export {
  skyboxSchema,
  sunSchema,
  waterSchema,
  fogSchema,
  cloudsSchema,
  ambientSchema,
  hemisphericLightSchema,
  cloudDeckSchema,
  moonSchema,
  weatherCellSchema,
  lightningSchema,
  lightShaftsSchema,
  decoratorSchema,
  trailSchema,
  soundSchema,
  effectSchema,
  sceneSchemas,
  SCENE_OMITTED,
} from './scene-schemas.js'
export { B3dFog, b3dFog } from './b3d-fog.js'
export { B3dClouds, b3dClouds } from './b3d-clouds.js'
export {
  softShadowTexture,
  shadowDecalMaterial,
  createShadowDecal,
  projectShadowDown,
} from './shadow-decal.js'
export type { ShadowDecalOptions, ProjectDownOptions } from './shadow-decal.js'
// Projected cloud shadows: one top-down texture sampled by world position (works over terrain).
export {
  CloudShadowMap,
  cloudShadowMapOf,
  projectShadowXZ,
  shadowWindowUv,
} from './cloud-shadows.js'
export type { CloudShadowBlob } from './cloud-shadows.js'
export { B3dAmbient, b3dAmbient } from './b3d-ambient.js'
export { LeafField } from './ambient-leaves.js'
export type { LeafFieldOptions } from './ambient-leaves.js'
// Garnish competes for ONE pool and switches OFF rather than thinning into a lie.
export { allocateAmbient, fillWeight, ratchetPool } from './ambient-budget.js'
export type {
  AmbientRequest,
  AmbientAllocation,
  AmbientEffect,
} from './ambient-budget.js'
// Atmosphere: fog is ALWAYS ON and systems lean on it (underwater / cloud / space).
export { compositeFog, approachFog, band } from './atmosphere.js'
export type { FogState, FogLayer } from './atmosphere.js'
export {
  B3dSphere,
  b3dSphere,
  B3dBox,
  b3dBox,
  B3dGround,
  b3dGround,
} from './b3d-primitives.js'
export { B3dButton, b3dButton } from './b3d-button.js'
export { B3dCollisions, b3dCollisions } from './b3d-collisions.js'
export { B3dPhysics, b3dPhysics } from './b3d-physics.js'
export { JoltPlugin } from './jolt-plugin.js'

// Input abstraction
export type { ControlInput, InputProvider } from './control-input.js'
export { emptyInput, CompositeInputProvider } from './control-input.js'
export { XRInputProvider } from './xr-input-provider.js'
export { B3dControllable } from './b3d-controllable.js'
export { B3dController, b3dController } from './b3d-controller.js'

// Virtual gamepad system
export type {
  VirtualGamepad,
  GamepadSource,
  InputMapping,
  MappingLabels,
  InputMappingDescriptor,
  ThrottleDetentConfig,
} from './virtual-gamepad.js'
export {
  emptyGamepad,
  mergeGamepads,
  bipedMapping,
  bipedMappingDescriptor,
  carMapping,
  carMappingDescriptor,
  aircraftMapping,
  aircraftMappingDescriptor,
  MappedInputProvider,
} from './virtual-gamepad.js'
export { STICK_UP_IS_POSITIVE } from './virtual-gamepad.js'
export { KeyboardGamepadSource, keyboardGamepad } from './keyboard-gamepad.js'
export { HardwareGamepadSource } from './hardware-gamepad.js'
export { TouchGamepadSource } from './touch-gamepad.js'
export type {
  TouchGamepadOptions,
  GamepadPointerKind,
} from './touch-gamepad.js'
export { gamepadSvg } from './gamepad-svg.js'
export type { GamepadSvgColors } from './gamepad-svg.js'
export {
  B3dGamepad,
  b3dGamepad,
  parseGamepadControls,
} from './glass-gamepad.js'
export { XrGamepadSource } from './xr-gamepad.js'
export type { ClusterConfig, ClusterAnchor } from './glass-gamepad.js'

// XR reference frames & spatial UI
export {
  XrFrames,
  EntityFrame,
  angleDelta,
  dampYaw,
  facingYaw,
  gazeReveal,
} from './xr-frames.js'
export type { FrameName, XrFramesOptions } from './xr-frames.js'
export {
  attachFramePanel,
  panelLayerScene,
  pickPanelLayers,
  placeholderPanelSvg,
  opacityWeightedGlow,
  excludeFromGlow,
  viewSpanAt,
} from './frame-panel.js'
export type {
  FramePanelSpec,
  AnchorSpec,
  AnchorPreset,
  ScreenAnchor,
  ScreenLength,
  PanelLayer,
} from './frame-panel.js'
export { B3dPanel, b3dPanel } from './b3d-panel.js'
export {
  barMenu,
  viewMenu,
  type BarMenu,
  type BarMenuConfig,
  type BarMenuOption,
  type ViewMenuOptions,
} from './bar-menu.js'
export { B3dMoon, b3dMoon } from './b3d-moon.js'
export {
  GAS_GIANTS,
  GAS_GIANT_GLSL,
  resolveGasGiant,
  gasGiantRgb,
  type GasGiantLook,
  type GasGiantOverrides,
} from './gas-giant.js'
export { ROCKY_WORLDS, resolveRockyWorld } from './rocky-world.js'
export { spaceBackdrop, PINNED_SKY_URL } from './space-backdrop.js'
export { SOL } from './sol.js'
export { createCarry, DRAG_THRESHOLD } from './carry.js'
export type {
  Carry,
  CarryTarget,
  CarryState,
  CarryVerdict,
  CarryResult,
} from './carry.js'
export {
  carryTile,
  carrySocket,
  carryPanel,
  carryOverlay,
  carryGhostLayer,
} from './carry-box.js'
export { createInventory } from './inventory.js'
export {
  inventoryPanel,
  inventoryDock,
  readSchematic,
  schematicFit,
} from './inventory-box.js'
export type {
  InventoryPanelOptions,
  InventoryPanel,
  InventoryDock,
  InventoryDockOptions,
  Schematic,
  SchematicSocket,
} from './inventory-box.js'
// Common nouns, so prefixed as they leave the barrel (CLAUDE.md: bare
// common nouns don't leak); the module itself keeps the short names.
export type {
  InventoryApi,
  InventoryDoc,
  InventoryDraft,
  InventoryEvent,
  InventoryPlugins,
  ItemKind,
  Item as InventoryItem,
  Slot as InventorySlot,
  Container as InventoryContainer,
  Holder as InventoryHolder,
  Place as InventoryPlace,
  Move as InventoryMove,
  MoveResult as InventoryMoveResult,
  Rule as InventoryRule,
  RuleContext as InventoryRuleContext,
  Recipe as InventoryRecipe,
  RecipePart as InventoryRecipePart,
} from './inventory.js'
export type {
  CarryDecorations,
  CarryTileOptions,
  CarrySocketOptions,
  CarryTileChild,
  CarrySocketChild,
  CarryPanel,
  CarryGhostLayer,
} from './carry-box.js'
export { layoutSystem } from './system-layout.js'
export type {
  SystemSpec,
  SystemPlanetSpec,
  SystemBodySpec,
  SystemLayout,
} from './system-layout.js'
export type { SpaceBackdropOptions } from './space-backdrop.js'
export type {
  RockyWorldLook,
  RockyCraters,
  RockyClimate,
} from './rocky-world.js'
export {
  decodeGray16Png,
  mapSurface,
  loadPlanetMap,
  type HeightMap,
  type MapSurfaceOptions,
} from './planet-map.js'
export { createMappedPlanetMaterial } from './planet-map-material.js'
export {
  createPlanetSurfaceMaterial,
  type PlanetSurfaceOptions,
} from './planet-surface-material.js'
export {
  createPlanetClouds,
  type PlanetStorm,
  type PlanetCloudOptions,
} from './planet-clouds.js'
export {
  uvToDirection,
  directionToUv,
  latLonToDirection,
  directionToLatLon,
  groundToUv,
  uvToGround,
  groundToDirection,
  directionToGround,
  groundToLatLon,
  latLonToGround,
  type SphereFrame,
  type PlanetSurface,
  climateForGlobe,
} from './sphere-map.js'
export {
  createGasGiantMaterial,
  type GasGiantMaterialOptions,
} from './gas-giant-material.js'
export { B3dTrail, b3dTrail, attachTrail } from './b3d-trail.js'
export { B3dWeatherCell, b3dWeatherCell } from './b3d-weather-cell.js'
export { B3dLightning, b3dLightning } from './b3d-lightning.js'
export { B3dLightShafts, b3dLightShafts } from './b3d-light-shafts.js'
export { SsaoController, ssaoActive } from './b3d-ssao.js'
export type { SsaoSetting, SsaoParams } from './b3d-ssao.js'
export { ProjectedAoController } from './b3d-ssao-projected.js'
export type { ProjectedAoParams } from './b3d-ssao-projected.js'
export type { WeatherSample, WeatherCell } from './weather.js'
export type { Trail, TrailOptions } from './b3d-trail.js'

// Character & input
export { B3dBiped, b3dBiped, AnimState } from './b3d-biped.js'
// Clip-name map for Quaternius UAL rigs — see b3d-biped.
export { ualAnimationStates } from './b3d-biped.js'
export type { AnimStateSpec } from './b3d-biped.js'
export { GameController, gameController } from './game-controller.js'
export { B3dInputFocus, inputFocus } from './b3d-input-focus.js'
export {
  gamepadState,
  gamepadText,
  xrControllers,
  xrControllersText,
} from './gamepad.js'

// Vehicles
export { B3dCar, b3dCar } from './b3d-car.js'
export { B3dTank, b3dTank } from './b3d-tank.js'
export { B3dGroundVehicle } from './b3d-ground-vehicle.js'
export {
  createVehicle,
  stepVehicle,
  shiftVehicle,
  heightGround,
  forwardSpeed,
  headingOf,
  chaseRigFor,
  CHASE_BOOM,
  chasePose,
  VEHICLE_STEP,
  type ChaseRig,
  type VehicleSpec,
  type VehicleState,
  type VehicleControls,
  type WheelSpec,
  type TrackSpec,
  type GroundRay,
  type GroundHit,
  type ContactState,
  type TrackState,
  type Suspension,
} from './ground-vehicle.js'
export {
  cityLayout,
  roadPiece,
  turnMask,
  SIDE_BIT as CITY_SIDE_BIT,
  type CityLayout,
  type CityLayoutOptions,
  type RoadCell,
  type RoadPiece,
  type Lot as CityLot,
  type Rect as CityRect,
  type Side as CitySide,
} from './city-layout.js'
export {
  B3dCity,
  b3dCity,
  cityGround,
  cityStart,
  KENNEY_CITY,
  type CityKit,
  type KitPiece,
} from './b3d-city.js'
export {
  createTraffic,
  recycleTraffic,
  TRAFFIC_CAR,
  stepTraffic,
  type Traffic as CityTraffic,
  type TrafficCar,
  type TrafficObstacle,
  type TrafficOptions,
  type TrafficViewer,
  type RecycleOptions as TrafficRecycleOptions,
} from './city-traffic.js'
export { roomLit, windowLights, WindowLightsPlugin } from './window-lights.js'
export {
  lightPool,
  LightPool,
  sceneDaylight,
  lampLevel,
  type VirtualLight,
} from './light-pool.js'
export {
  lightPollutionSource,
  lightPollutionAt,
  pollutionFrom,
  combinePollution,
  type LightPollutionSource,
} from './light-pollution.js'
export { B3dAircraft, b3dAircraft } from './b3d-aircraft.js'

// The callback-naming shim. `handleX` is the name across this library; `onX`
// still works and warns once. Exported so an adopter's own widgets can follow
// the same rule rather than re-implementing it.
export { handlerOf, resetHandlerWarnings } from './handler-of.js'

// Colour — the pure model (headless-safe) and the picker over it.
export {
  parseColor,
  formatColor,
  rgbToHsv,
  hsvToRgb,
  wrapHue,
  luminance,
  contrastInk,
} from './color.js'
export type { Rgba, Hsva } from './color.js'
export { color3d } from './color-field.js'
export { terrainEditor3d } from './terrain-editor.js'
export type {
  TerrainSettings,
  TerrainEditor3dOptions,
  TerrainEditorField,
} from './terrain-editor.js'
export type { Color3dOptions, ColorField } from './color-field.js'
export {
  colorPicker3d,
  type ColorPicker,
  type ColorPicker3dOptions,
} from './color-picker.js'
export {
  COLOR_PRESETS,
  SWATCH_KEY,
  presetList,
  presetCells,
  createSwatchStore,
  sharedSwatches,
  type ColorPreset,
  type SwatchStore,
  type SwatchStorage,
} from './color-presets.js'
export {
  materialPicker3d,
  type MaterialPicker,
  type MaterialPicker3dOptions,
} from './material-picker.js'
export {
  PBR_MATERIAL,
  CLOTH_MATERIAL,
  MATERIAL_COLLECTIONS,
  materialAsType,
  applyMaterialPreset,
  isMaterialPreset,
  registerMaterialType,
  materialType,
  materialTypes,
  resolveMaterial,
  resolveInput,
  writeInput,
  withInput,
  canonicalMaterial,
  materialSwatch,
  materialRecipeSchema,
  validateMaterial,
  type MaterialRecipe,
  type MaterialInput,
  type MaterialInputDecl,
  type MaterialInputKind,
  type MaterialTypeDecl,
  type MaterialCollection,
  type MaterialPreset,
  type ResolvedInput,
} from './material-recipe.js'
export { applyMaterial } from './material-apply.js'

// Manipulator — pure maths, the Babylon handles, and the element that wires them
export {
  NO_TRANSFORMS,
  RING_BASIS,
  WORLD_FRAME,
  noTransforms,
  otherAxes,
  axisVector,
  axisComponent,
  axisClosestApproach,
  angleAboutAxis,
  rayPlanePoint,
  rayPerpendicularDistance,
  snap,
  snapVec3,
  normaliseDegrees,
  scaleFactor,
  beginDrag,
  updateDrag,
  commitTransform,
  dragChanged,
} from './manipulator.js'
export type {
  Axis,
  AxisFrame,
  ComposeRotation,
  Drag,
  DragOptions,
  Grip,
  GripKind,
  ManipulatorRay,
  ManipulatorTransform,
  TransformSet,
} from './manipulator.js'
export {
  createHandles,
  composeRotation,
  axisFrameOf,
  HANDLE_TAG,
  DRAWN_TAG,
  NEAR_RADIUS,
} from './manipulator-view.js'
export type { HandlesView } from './manipulator-view.js'
export { B3dManipulator, b3dManipulator } from './b3d-manipulator.js'

// Angle and arc dials — a circle has no ends, so a slider track is the wrong shape
export {
  arcOf,
  arcStart,
  arcEnd,
  arcContains,
  arcWithinArc,
  arcComplement,
  clampAngleToArc,
  clampArc,
  dragArc,
  nearestArcGrip,
  FULL_CIRCLE,
} from './arc.js'
export type { Arc, ArcGrip, ArcLimits } from './arc.js'
export { angle3d, arc3d } from './angle-field.js'
export { B3dBeacon, b3dBeacon, beaconOwner } from './b3d-beacon.js'
export { B3dElevator, b3dElevator } from './b3d-elevator.js'

// Choosing one of many — a select is right for five options and a scroll for 500
export { picker3d, matchesQuery, groupsOf } from './picker.js'

// One wind for the scene, and provinces that bend it
export {
  NO_WIND,
  addWind,
  scaleWind,
  windSpeed,
  windFromPolar,
  windToPolar,
  windAt,
  provinceInfluence,
  gustAt,
  waterWind,
} from './wind.js'
export type { Wind, ProvinceWind } from './wind.js'
export type { Picker, PickerOption, Picker3dOptions } from './picker.js'
export type {
  AngleField,
  ArcField,
  Angle3dOptions,
  Arc3dOptions,
} from './angle-field.js'

// SVG widgets (DOM-overlay or in-scene panels)
export {
  panel3d,
  titled3d,
  fitPanel,
  row3d,
  label3d,
  text3d,
  textBlock3d,
  button3d,
  iconBar3d,
  tabs3d,
  foldSections,
  toggle3d,
  slider3d,
  select3d,
  list3d,
  menu3d,
  openMenu3d,
  spinner3d,
  progress3d,
} from './widgets3d.js'
// Curve editing — the pure model, and the `curve3d` widget over it. A province
// is a footprint plus one curve per layer (PROVINCE-DESIGN.md).
export {
  normalizeCurve,
  evaluateCurve,
  blendSample,
  flipCurve,
  movePoint,
  insertPoint,
  deletePoint,
  pointAt,
  curvePresets,
  presetsFor,
  defaultCurve,
  polygonExtent,
  polygonVertices,
  closePolygon,
  moveVertex,
  isStarShaped,
  MIN_EXTENT,
  // The PARAMETERISED builders, so a consumer is not stuck with the preset's
  // frozen arguments — `plateauFalloff(0.8)` and `messyNgon(24, 0.3, 7)` are
  // the point of them having parameters at all.
  //
  // `linear`/`constant`/`stepped`/`circle`/`rim`/`easeIn`… stay OUT: bare common
  // nouns, and `circle` would shadow svgElements' `circle`. That is the barrel
  // rule in CLAUDE.md, and they are reachable via `defaultCurve(kind)` and
  // `presetsFor(kind)`.
  ngon,
  messyNgon,
  shelfAndMountains,
  desertTerraces,
  plateauFalloff,
  smoothEdge,
  abruptEdge,
} from './curve.js'
export { moveMarker, normalizeMarkers, MIN_SPLIT_GAP } from './curve.js'
/*
`isTextEntry` — ask before a global listener claims a key. A page is not only a
scene, and `a` is a strafe key.
*/
export { isTextEntry } from './text-entry.js'
/*
Bone masking for layered animation — walk and aim at once. Pure: a hierarchy is
names and parents.
*/
export {
  UPPER_BODY_ROOTS,
  BONE_SOCKETS,
  boneMask,
  complementMask,
  descendantDepths,
  findBone,
  maskIsEmpty,
  type BoneMask,
  type BoneMaskOptions,
  type BoneNode,
  type MaskMode,
} from './bone-mask.js'
export {
  boneHierarchy,
  layerGroups,
  layerOnUpperBody,
  type AnimationTier,
  type LayeredAnimation,
} from './animation-layers.js'
export {
  clearOfBand,
  easeDistance,
  fitChase,
  fitDistance,
  forceFirstPerson,
  type Band,
  type ChaseFit,
  type EaseOptions,
  type FitOptions,
} from './camera-fit.js'
export { playground, type PlaygroundOptions } from './playground.js'
export { B3dCloudDeck, b3dCloudDeck } from './b3d-cloud-deck.js'
// Rocks and trees on the terrain, by budget and climate.
export { B3dDecorator, b3dDecorator } from './b3d-decorator.js'
export {
  shoreGrid,
  shoreGridLines,
  shoreData,
  iceCover,
  iceSolid,
  iceBears,
  iceSide,
  ICE_BEARS,
  SHORE_DISTANCE_MAX,
  type IceSide,
  type ShoreGrid,
} from './water-shore.js'
export {
  rockGeometry,
  rockFromName,
  rockNames,
  ROCK_KINDS,
  type RockOptions,
  type RockGeometry,
} from './procedural-rock.js'
export {
  scatterPlacements,
  clumpAt,
  autoClumpSize,
  NATURE_RULES,
  NATURE_ROLES,
  roleFor,
  type DecorationRole,
  ROCK_RULES,
  NearIndex,
  NATURE_KIT_RULES,
  type ScatterRule,
  type ScatterClimate,
  type ScatterOptions,
  type Placement,
} from './scatter.js'
export {
  cloudField,
  cloudOpacity,
  type CloudFieldOptions,
} from './cloud-field.js'
export {
  BRIGHT_GAMMA,
  FACE_NAMES,
  PACKED_CAPACITY,
  PACKED_FLAG,
  STAR_PALETTE,
  decodeTexel,
  dirToFace,
  encodeStarfield,
  faceToDir,
  paletteGlsl,
  paletteIndex,
  spectralGlsl,
  spectralRamp,
  spectralValue,
  type DecodedObject,
  type EncodedStarfield,
  type FaceUv,
  type SkyObject,
} from './starfield-codec.js'
export { pngEncode, pngDecode, crc32, adler32 } from './png.js'
export type { RgbaImage } from './png.js'
export {
  bakeSkyboxCube,
  bakeSkyPair,
  starsFromGalaxy,
  starsFromVoxelGalaxy,
  facesToPngs,
  facesToZip,
  defaultBakePose,
  SHIPPED_SKY,
  type BakedFace,
  type SkyboxBakeOptions,
} from './skybox-baker.js'
export {
  SurroundingsProbe,
  makeProbeScratch,
  readSurroundings,
  type ProbeScratch,
  type ReadOptions,
} from './surroundings-probe.js'
export {
  DEFAULT_HEIGHTS,
  bearingIndex,
  exposure,
  inShelter,
  makeSurroundings,
  muzzleClearance,
  peekSide,
  sampleAt,
  setSample,
  shelterFrom,
  shuffleToward,
  stanceFor,
  type PeekSide,
  type Shelter,
  type ShelterOptions,
  type Stature,
  type Surroundings,
  type SurroundingsSpec,
} from './surroundings.js'
export {
  DEFAULT_AIM_LIMITS,
  aimAuthority,
  aimDirection,
  aimPoseWeights,
  aimToward,
  aimWobble,
  bodyCatchUp,
  clampAim2,
  relaxAim,
  stepAim,
  wrapDeg,
  type Aim,
  type AimLimits,
  type AimPoseWeights,
  type AimStep,
  type AimVec,
  type BodyCatchUp,
} from './aim.js'
export {
  B3dCrowd,
  b3dCrowd,
  VatPlugin,
  buildBenchFigure,
  writeVatTextures,
  type VatBake,
} from './b3d-crowd.js'
/*
Vertex-animation substrate — the crowd/fauna primitive. Pure layout and phase
maths; the Babylon half is `b3d-crowd`.
*/
export {
  framePair,
  framesForClip,
  phaseAt,
  socketLayout,
  socketTexel,
  vatBytes,
  vatLayout,
  vatTexel,
  type FramePair,
  type SocketLayout,
  type VatClip,
  type VatLayout,
} from './vertex-animation.js'
/*
Spatial-panel placement. `Orbit`/`orbitPosition`/`orbitOf` are the seat, and the
bare nouns stay namespaced-by-prefix rather than exported as `centre`/`position`
— see CLAUDE.md on common nouns leaking from the barrel.
*/
export {
  ORBIT_MAX_AZIMUTH,
  ORBIT_RUBBER_BAND,
  bandOrbit,
  orbitFromAim,
  rubberBand,
  ORBIT_MAX_ELEVATION,
  ORBIT_MIN_ELEVATION,
  angularHeight,
  clampOrbit,
  orbitCentre,
  orbitClampedBy,
  orbitFromDirection,
  orbitOf,
  orbitPosition,
  type Orbit,
  type OrbitVec3,
} from './panel-orbit.js'
// Serialisation contract with tosijs-3d-ensemble (#61): plain JSON, canonical
// bytes, a validator that never throws, and a schema fragment to dispatch on.
export {
  readCurve,
  canonicalCurve,
  validateCurve,
  curveSchema,
  CURVE_PRECISION,
} from './curve.js'
export type { SerializedCurve, CurveIssue } from './curve.js'
export type { CurveKind, CurvePreset } from './curve.js'
export { curve3d, curveMarkers } from './curve-field.js'
// The composite: several curves sharing ONE pair of split markers, so the
// invariant lives where it can be enforced (see #61 Q5a).
export { curveProgram3d, PROGRAM_CHANNELS } from './curve-program.js'
// The whole lamp — static properties plus the program, as one field.
export { lightEditor3d } from './light-editor.js'
export type { LightEditor3dOptions, LightEditorField } from './light-editor.js'
/*
A lamp as DATA, from a module with no DOM and no Babylon — so a consumer can
validate, canonicalise and schema-describe one in a headless runner without
instantiating a UI. (Importing the EDITOR pulls in tosijs and needs
HTMLElement; ensemble's test suite has no browser.)
*/
export {
  lightColor,
  lightSettingsSchema,
  canonicalLight,
  validateLight,
  DEFAULT_LIGHT,
} from './light-settings.js'
export type { LightSettings, LightKind } from './light-settings.js'
export type {
  CurveProgram3dOptions,
  CurveProgramField,
  ProgramChannel,
} from './curve-program.js'
export { iconGrid3d } from './icon-grid.js'
export type {
  IconGrid,
  IconGrid3dOptions,
  IconGridItem,
  IconGridChange,
} from './icon-grid.js'
export { footprint3d } from './footprint-field.js'
export type { Footprint3dOptions, FootprintField } from './footprint-field.js'
export type { Curve3dOptions, CurveField, CurveMarkers } from './curve-field.js'

// A coordinate on ONE row — the density win for inspector panels. Its own
// module so it tree-shakes, and top-level like the other `*3d` widgets.
export { vector3d, euler3d, wrapDegrees } from './vector-field.js'
/*
`WidgetHost` is exported deliberately, not incidentally: it is the type a
custom widget must name to implement `setHost`, and without it a consumer
could receive a host but never declare the parameter (tosijs-3d#59).
*/
export type {
  Widget3d,
  WidgetHost,
  MenuAction,
  PopupHandle,
  Tab3d,
  Dynamic,
  Spinner3d,
  Progress3d,
  Bindable,
  Panel3d,
} from './widgets3d.js'
export {
  panelFitWidth,
  panelFit,
  panelHeight,
  rowColumns,
  alignOffset,
  stackLayout,
  clampScroll,
  wrapText,
  wrapByMeasure,
  cssFont,
  textMeasurer,
  measureTextWrap,
  measureTextWidth,
  valueToFraction,
  fractionToValue,
} from './widgets3d-layout.js'
export type {
  StackLayout,
  FontSpec,
  PanelFit,
  RowColumn,
} from './widgets3d-layout.js'

// SVG material system
export { SvgTexture, svgTextureCost } from './svg-texture.js'
export type { SvgTextureOptions } from './svg-texture.js'
export { B3dSvgPlane, b3dSvgPlane, panelScene } from './b3d-svg-plane.js'
export type {
  PanelSceneOptions,
  PanelGestureEvent,
  PanelGestureAction,
} from './b3d-svg-plane.js'
export {
  panelGesture,
  uvToViewBox,
  planeLocalToViewBox,
} from './b3d-svg-plane.js'

// Icon proxy (svgIcons.name() → SVG ElementCreator) over the generated icon set
export {
  svgIcons,
  createSvgIcons,
  iconGlyph,
  iconNames,
  iconAliases,
  iconData,
  // Consumers own icons too: `registerIcons` adds names every WIDGET can
  // resolve, which a private `createSvgIcons` set cannot do.
  registerIcons,
  iconExists,
  isRegisteredIcon,
  type SvgIconCreator,
} from './svg-icons.js'
export {
  parseStyleSuffixes,
  mergeIconStyle,
  type IconStyle,
  type ParsedIconName,
} from './icon-name.js'

// ---------------------------------------------------------------------------
// The SVG UI surface, namespaced as `ui.*` — `ui.box`, `ui.table`, `ui.keyboard`.
// These are COMMON NOUNS (box, table, button, edit, insert…); exporting them
// bare from a library barrel collides with every consumer's own vocabulary, so
// the family lives in one container and the top level stays clean. Types stay
// top-level (PascalCase — no pollution, and types can't live on a const).
// Decided at the 0.6.0 rc gate — see UI-DESIGN-NOTES.
// ---------------------------------------------------------------------------
import { flowLayout, nearestInDirection, placePopup } from './flow-layout.js'
import { anchorLayout, anchorLength } from './anchor-layout.js'
import {
  box,
  textBlock,
  inlineIcon,
  blockItem,
  inlineItem,
  button,
  svgPoint,
} from './box.js'
import { surface, openMenu } from './surface.js'
import { widgetBox, widgetChild } from './widget-box.js'
import {
  edit,
  insert,
  backspace,
  deleteForward,
  moveCaret,
  moveTo,
  selectAll,
  selectedText,
  selectionRange,
  hasSelection,
  length as editLength,
} from './text-edit.js'
import { selectionIcon, applySelection } from './selection.js'
import {
  resolveColumns,
  visibleRows,
  contentHeight as tableContentHeight,
  maxScroll as tableMaxScroll,
  rowAt,
  columnAt,
} from './table-layout.js'
import { gamepadFocus, createFocusPulse } from './gamepad-focus.js'
import {
  keyboard,
  inputField,
  fieldGroup,
  autoKeyboardEnabled,
  setAutoKeyboard,
} from './keyboard.js'
import {
  keyLayout,
  accentsFor,
  hasAccents,
  keyRects,
  keyboardHeight,
  keyAt,
} from './key-layout.js'
import { table } from './table.js'

export const ui = {
  // flow-layout core (pure)
  flowLayout,
  nearestInDirection,
  placePopup,
  // anchor-layout core (pure): positioned and layered
  anchorLayout,
  anchorLength,
  // box — the flow container (paint / resize / scroll)
  box,
  textBlock,
  inlineIcon,
  blockItem,
  inlineItem,
  button,
  svgPoint,
  // surface — content + overlay popups; cascade menus + persistent panels
  surface,
  openMenu,
  // widgets3d controls inside a box/surface
  widgetBox,
  widgetChild,
  // data table + its pure geometry
  table,
  resolveColumns,
  visibleRows,
  tableContentHeight,
  tableMaxScroll,
  rowAt,
  columnAt,
  // on-screen keyboard + input field, and the pure keyboard model
  keyboard,
  inputField,
  fieldGroup,
  // The SHARED on-screen-keyboard preference the field glyph toggles — exported
  // so an app can seed it, persist it, or drive it from its own settings.
  autoKeyboardEnabled,
  setAutoKeyboard,
  keyLayout,
  accentsFor,
  hasAccents,
  keyRects,
  keyboardHeight,
  keyAt,
  // pure text-editing model (code-point aware)
  edit,
  insert,
  backspace,
  deleteForward,
  moveCaret,
  moveTo,
  selectAll,
  selectedText,
  selectionRange,
  hasSelection,
  editLength,
  // selection-as-icon
  selectionIcon,
  applySelection,
  // gamepad D-pad → focus traversal
  gamepadFocus,
  createFocusPulse,
}

// Types for the ui.* family (top-level — PascalCase, no namespace pollution)
export type {
  FlowItem,
  FlowOptions,
  FlowBox,
  FlowResult,
  PopupSide,
} from './flow-layout.js'
export type {
  AnchorItem,
  AnchorBox,
  AnchorLength,
  AnchorResult,
} from './anchor-layout.js'
export type {
  Box,
  BoxChild,
  BoxChildState,
  BoxOptions,
  PointerKind,
} from './box.js'
export type { Surface, Popup, MenuItem } from './surface.js'
export type { EditState } from './text-edit.js'
export type { SelectionMode } from './selection.js'
export type { ColumnSpec, ColumnRect, RowWindow } from './table-layout.js'
export type {
  FocusTarget,
  FocusPulse,
  GamepadFocusOptions,
} from './gamepad-focus.js'
export type {
  Keyboard,
  InputField,
  KeyboardOptions,
  InputFieldOptions,
} from './keyboard.js'
export type { KeyboardMode, KeyAction, KeyDef, KeyRect } from './key-layout.js'

// Procedural biome shader (TERRAIN-SHADER-DESIGN.md): pure chart model + plugin
export {
  mantaAxes,
  planetaryAxes,
  chartUV,
  cellBlend,
  slopeMask,
  photicFactor,
  chartTemperature,
  planetTemperature,
  CELSIUS_PER_UNIT,
} from './biome-chart.js'
export type { BiomeChartConfig } from './biome-chart.js'
// Slope profiles — levels adjustments for terrain, localizable across regions
export {
  cliffProfile,
  beachProfile,
  rollingProfile,
  mesaProfile,
  terraceProfile,
  blendProfiles,
  profileField,
} from './slope-profile.js'
export type { LocalizedFilter } from './slope-profile.js'
export type { SphereCraterOptions } from './landform.js'
// Landform options as JSON Schema (tosijs-3d#119)
export {
  landformSchemas,
  volcanoSchema,
  impactCraterSchema,
  padSchema,
  gulleySchema,
  coverSchema,
  landformDerived,
} from './landform-schemas.js'
export {
  volcano,
  impactCrater,
  craterField,
  craterProfile,
  sphereCraters,
  pad,
  gulley,
  cover,
  composeLandforms,
  mergeProvinces,
  // A field knows where it stops — see landform's "A field knows where it stops".
  withExtent,
  extentOf,
  touchesExtent,
  unionExtent,
  circleExtent,
  corridorExtent,
} from './landform.js'
// Volumetric patch substrate (tunnels/caverns — see TODO 0.7.0)
export { latticeHash, latticePoint, extractChunk } from './sdf-lattice.js'
/*
The CARVE family lives in a container for the same reason the `ui` family does
(see the note above): `box`, `sphere`, `tube`, `union` are COMMON NOUNS, and a
library barrel that exports them bare collides with every consumer's own
vocabulary. `box` was the sharp end — a second one here would have shadowed
`ui.box`, which is exactly the collision the 0.6.0 rc gate namespaced `ui` to
prevent. Caught by the 0.7.0 gate while the window was still free.
*/
import {
  applyCarve,
  sphere,
  capsule,
  tube,
  box as carveBox,
  union,
  smoothUnion,
  flange,
  subtract,
  intersect,
  roughen,
  warp,
  shaft,
} from './carve.js'
export const carve = {
  applyCarve,
  sphere,
  capsule,
  tube,
  box: carveBox,
  union,
  smoothUnion,
  flange,
  subtract,
  intersect,
  roughen,
  warp,
  shaft,
}
export type { Carve, NoiseOptions, Vec3Like } from './carve.js'
export {
  terrainDensity,
  composePatches,
  circleFootprint,
  marginBlend,
} from './patch-field.js'
export type { PatchField, Footprint } from './patch-field.js'
export type {
  SdfField,
  LatticeConfig,
  ChunkSpec,
  ExtractedMesh,
} from './sdf-lattice.js'
export type {
  AuthoredLandform,
  Vent,
  Extent,
  Bounded,
  LandformFn,
  ProvinceFn,
  VolcanoOptions,
  CraterOptions,
  CraterFieldOptions,
  PadOptions,
  GulleyOptions,
  CoverOptions,
} from './landform.js'
export {
  BiomePlugin,
  attachBiomePlugin,
  setBiomeLite,
  climateNoiseAt,
  groundClimateAt,
  biomeLite,
  setBiomeComputedNoise,
  biomeComputedNoise,
  defaultBiomeParams,
  MANTA_PALETTE,
  LAVA_PALETTE,
  CRYOVOLCANIC_PALETTE,
  seasonOf,
} from './biome-plugin.js'
export type { BiomeParams, BiomeRole } from './biome-plugin.js'

// Effects & interaction
export { B3dParticles, b3dParticles } from './b3d-particles.js'
// Effects: particles, light and sound as layers on one clock (EFFECTS-DESIGN).
export { B3dEffect, b3dEffect } from './b3d-effect.js'
export { EffectPlayer } from './effect-player.js'
export type { EffectPlayerOptions } from './effect-player.js'
export {
  EFFECT_PRESETS,
  effectPreset,
  VOLCANO_STATES,
} from './effect-presets.js'
export {
  EFFECT_RECIPE_VERSION,
  SPRITES as EFFECT_SPRITES,
  VOICES as EFFECT_VOICES,
  layerAt,
  lightScore,
  rankLights,
  soundDelay,
  validateEffectRecipe,
  effectRecipeSchema,
} from './effect-recipe.js'
export type {
  EffectRecipe,
  EffectLayer,
  ParticleLayer,
  LightLayer,
  SoundLayer,
  EffectCurve,
  SpriteKind,
  VoiceKind,
  LayerState,
} from './effect-recipe.js'
export { B3dSound, b3dSound } from './b3d-sound.js'
export { B3dTrigger, b3dTrigger } from './b3d-trigger.js'
// Interactive: the substrate for touching a thing — doors, knobs, switches, locks.
export { B3dInteractive, b3dInteractive } from './b3d-interactive.js'
export {
  InteractiveBehavior,
  nearestInteractive,
  useNearest,
} from './interactive-behavior.js'
export type {
  InteractionInfo,
  InteractiveHost,
  InteractiveConfig,
} from './interactive-behavior.js'
export {
  interactStep,
  newInteractState,
  activationVeto,
  withinReach,
} from './interaction.js'
export type {
  InteractPhase,
  InteractState,
  InteractInput,
  InteractResult,
} from './interaction.js'
// Death: the exit a crash needs (wreckage, orbit camera, respawn panel)
export { B3dDeath, b3dDeath } from './b3d-death.js'
// Wreck fall: the pure tumbling-descent model b3d-death drops a corpse with.
export { newWreckFall, wreckFallStep, tumbleAxis } from './wreck-fall.js'
// Buoyancy: the pure vertical model behind the biped's swimming.
export {
  buoyantStep,
  submergedFraction,
  equilibriumSubmersion,
  isSwimming,
} from './buoyancy.js'
export type { BuoyancyParams } from './buoyancy.js'
// Swim aim: the pure model behind look-directed swimming.
export {
  clampAim,
  aimFromLook,
  integrateAim,
  easeAim,
  aimTarget,
} from './swim-aim.js'
export type { WreckFallState, WreckFallParams } from './wreck-fall.js'
// Spawner: keeps the world populated with encounters (prefab + a rule).
export { B3dSpawner, b3dSpawner } from './b3d-spawner.js'
// Formations: pure placement patterns for an encounter's members.
export { ring, vee, escorts, line, at } from './formations.js'
export type { Offset, RingOptions, VeeOptions } from './formations.js'
// Prefabs: a named factory that instantiates a package of stuff at a pose (remains, loot,
// spawner payloads, pickups). See prefab.ts.
export { definePrefab, getPrefab, prefabNames, spawnPrefab } from './prefab.js'
export type { Prefab, PrefabContext, PrefabVec3 } from './prefab.js'
export { explodeMesh } from './b3d-exploder.js'
export type { ExplodeOptions } from './b3d-exploder.js'

// Procedural
export { PerlinNoise } from './perlin-noise.js'
export {
  MersenneTwister,
  PRNG,
  Xoshiro128,
  CheapPRNG,
} from './mersenne-twister.js'
export { B3dTerrain, b3dTerrain } from './b3d-terrain.js'
export { B3dPlanet, b3dPlanet } from './b3d-planet.js'
export { B3dStar, b3dStar } from './b3d-star.js'
export { B3dBlackHole, b3dBlackHole } from './b3d-black-hole.js'
export { B3dGalaxy, b3dGalaxy } from './b3d-galaxy.js'
export { B3dStarSystem, b3dStarSystem } from './b3d-star-system.js'
export {
  generateStarSystem,
  starTypeData,
  randomName,
  capitalize,
  romanNumeral,
} from './galaxy-data.js'
export type {
  StarData,
  PlanetData,
  StarSystemData,
  GalaxyData,
  GalaxyOptions,
  StarTypeInfo,
  NebulaData,
} from './galaxy-data.js'
// THE galaxy (GALAXY-DESIGN.md).
export { voxelGalaxy } from './voxel-galaxy.js'
export type {
  VoxelGalaxy,
  VoxelStar,
  VoxelGalaxyOptions,
  GalaxyViewOptions,
} from './voxel-galaxy.js'
export { sampleSpiral, starNameFor } from './galaxy-data.js'
// What makes a dim star INTERESTING (HI <= 2) — the harness-learned split.
export {
  bestHIOf,
  isInteresting,
  interestingMix,
  boringMix,
  interestingShare,
  hiFingerprint,
  INTERESTING_MAX_HI,
} from './star-populations.js'
export { starAddress, parseStarAddress } from './voxel-galaxy.js'
export type { GradientFilter, ControlPoint } from './gradient-filter.js'
export {
  PiecewiseLinearFilter,
  identityFilter,
  plateauFilter,
} from './gradient-filter.js'
export { GradientEditor, gradientEditor } from './gradient-editor.js'
export {
  TorusSampler,
  SphereSampler,
  CylinderSampler,
} from './surface-sampler.js'
export type { SurfaceSampler, Vec3 } from './surface-sampler.js'
// Combat — pure, deterministic models (see COMBAT-DESIGN.md)
export {
  makeResource,
  drain,
  refill,
  regenTick,
  isEmpty,
  isFull,
  fraction,
} from './resource.js'
export type { Resource } from './resource.js'
export { CombatWorld, DEFAULT_CHAIN_DELAY } from './destroyable.js'
export type {
  Destroyable,
  DestroyableSpec,
  ChainLink,
  CombatEvent,
  Cause,
} from './destroyable.js'
export { B3dDestroyable, b3dDestroyable } from './b3d-destroyable.js'
export { B3dProp, b3dProp } from './b3d-prop.js'
export { loadLibraryMesh } from './library-mesh.js'
export type { LibraryMeshRequest } from './library-mesh.js'
export { DestroyableBehavior } from './destroyable-behavior.js'
export type { DeathOutcome, DestroyableHost } from './destroyable-behavior.js'
export {
  B3dWarhead,
  b3dWarhead,
  detonateWarhead,
  explosionFx,
} from './b3d-warhead.js'
export {
  B3dLauncher,
  b3dLauncher,
  spawnProjectile,
  spawnMissile,
} from './b3d-launcher.js'
export type { ProjectileOpts, MissileOpts, Impact } from './b3d-launcher.js'
export { createMakers } from './make-mesh.js'
export { roundedRectGeometry, signedArea } from './rounded-rect.js'
export type { RoundedRectOptions, RoundedRectGeometry } from './rounded-rect.js'
export { openPopup } from './popup-surface.js'
export type { PopupSurface, PopupSurfaceOptions } from './popup-surface.js'
export type { Vector3dOptions, VectorField } from './vector-field.js'
export type { Makers, MakeOptions, MakeOwner } from './make-mesh.js'
export { B3dTurret, b3dTurret } from './b3d-turret.js'
export { B3dRadarBlip, b3dRadarBlip } from './b3d-radar-blip.js'
export { B3dRadar, b3dRadar } from './b3d-radar.js'
export { Radar, coneDotFromDegrees, isOpposed } from './radar.js'
export type { RadarContact, RadarTrack, RadarParams } from './radar.js'
export {
  steerToward,
  proNav,
  interceptLead,
  boostAuthority,
  gAdd,
  gSub,
  gScale,
  gDot,
  gCross,
  gLen,
  gNormalize,
} from './guidance.js'
export {
  canonicalize,
  normalizeScale,
  findCenterOfGravity,
  findBarrel,
  findMuzzle,
  applyCenterOfGravity,
} from './model-transform.js'
export { aoeFalloff, resolveAoe, dist3 } from './warhead.js'
export type { WarheadSpec, AoeTarget } from './warhead.js'
export { ballisticStep, predictPath, ballisticAim } from './ballistics.js'
export type {
  BallisticParams,
  BallisticState,
  PredictOptions,
} from './ballistics.js'

// Data table types (the value lives at ui.table)
export type { Table, TableRow, TableOptions } from './table.js'

export {
  modeForType,
  isValidForType,
  commitValueForType,
} from './key-layout.js'
export type { FieldType } from './key-layout.js'

export { w3dTheme, setW3dTheme, withTheme } from './w3d-theme.js'
export type { W3dTheme } from './w3d-theme.js'
export {
  waterNormalTexture,
  tileHeight,
  writeNormalMap,
} from './water-normal.js'
export { themeEditor, FONT_STACKS } from './theme-editor.js'
export type { ThemeEditorOptions, ThemeEditorElement } from './theme-editor.js'
export {
  registerSvgFont,
  unregisterSvgFont,
  fontFaceCss,
  svgFontStyle,
  base64OfBytes,
} from './embed-font.js'
export type { EmbeddedFont } from './embed-font.js'
// The orbit camera's view controls: orbit, pan (shift-drag, trackpad, two fingers), zoom (#52).
export { touchOrbit } from './touch-orbit.js'
export {
  inspectView,
  inspectStage,
  seamOverlay,
  INSPECT_FACES,
  type InspectFace,
  type InspectView,
  type InspectViewOptions,
  type InspectStage,
  type InspectStageOptions,
} from './inspect-view.js'
export {
  seamMaterial,
  withBarycentrics,
  quadDiagonals,
  SeamPlugin,
  BARY_KIND,
  type SeamMaterialOptions,
} from './seam-material.js'
export {
  twoFingerGesture,
  wheelClassifier,
  type WheelKind,
  type WheelSample,
  type TwoFingerGesture,
  type TwoFingerStep,
  type TouchPoint,
} from './touch-gesture.js'
export { costSweep, formatSweep, formatRuns, recordRun } from './cost-sweep.js'
export { debugSink, sendDebug } from './debug-sink.js'
export { nearFirst, drawKey } from './draw-order.js'
export { noiseVolume, sampleNoiseVolume } from './noise-volume.js'
export {
  CLIMATE_CELLS,
  climateLattice,
  climateNoise,
  climateOctave,
} from './climate-noise.js'
export type { SweepRow, SweepOptions, SweepRun } from './cost-sweep.js'
export {
  humanoidRoles,
  retargeter,
  rotationBetween,
  ROLES,
  FINGERS,
} from './retarget.js'
export type {
  Role,
  Side as RetargetSide,
  Limb,
  Finger,
  FingerJoint,
  NamedBone,
  RestBone,
  Skeleton as RetargetSkeleton,
  Retargeter,
  RetargetOptions,
} from './retarget.js'
export {
  readSkeleton,
  retargetClip,
  retargetClips,
} from './retarget-babylon.js'
export type { ReadSkeleton } from './retarget-babylon.js'
export {
  RIG_RECIPE_VERSION,
  rigRecipeSchema,
  STRAND_DEFAULTS,
  strandDirection,
  strandPoints,
  strandPath,
  setStrandPath,
  pathLength,
  validateRigRecipe,
} from './rig-recipe.js'
export type {
  RigRecipe,
  SpringRecipe,
  BoneSpringRecipe,
  StrandSpringRecipe,
  ColliderRecipe,
  SpringParamsRecipe,
} from './rig-recipe.js'
export { applyRigRecipe } from './rig-recipe-babylon.js'
export type { AppliedRig, AppliedSpring } from './rig-recipe-babylon.js'
export {
  fitSkeleton,
  fittedRestBones,
  meshQuery,
  objTriangles,
  skinSegments,
  MARKER_NAMES,
  rigSilhouette,
  guessMarkers,
} from './rig-fit.js'
export type {
  FitOptions as SkeletonFitOptions,
  FittedJoint,
  FittedRig,
  MarkerName,
  Markers,
  MeshQuery,
  Point2,
  Silhouette,
  Span,
} from './rig-fit.js'
export { markerField3d, MARKER_LABELS } from './rig-markers.js'
export type { MarkerField, MarkerField3dOptions } from './rig-markers.js'
export { boneHeat } from './bone-heat.js'
export {
  buildRig,
  buildBody,
  gatherTriangles,
  loadRigTriangles,
  autoRig,
} from './rig-build.js'
export type {
  BuiltRig,
  BuiltBody,
  RigTriangles,
  AutoRigged,
} from './rig-build.js'
export { bodyFromMarkers } from './rigged-body.js'
export type {
  RiggedBody,
  RiggedBodyOrigin,
  BodyTriangles,
  BodySurface,
  BodyEye,
} from './rigged-body.js'
export {
  isEyeName,
  splitEyes,
  eyeCentre,
  eyeUvs,
  lidBlink,
} from './rigged-body.js'
export { mountTailorTool } from './tailor-tool.js'
export { chooseFile } from './choose-file.js'
export {
  loadSkinnedBody,
  soleVertices,
  lowestOf,
  surfaceMaterial,
} from './rig-build.js'
export type {
  TailorTool,
  TailorToolOptions,
  TailorCut,
  TailorState,
  TailoredGarment,
} from './tailor-tool.js'
export { loadHumanPack, decodeHumanTarget } from './human-pack.js'
export type {
  HumanPack,
  HumanPackManifest,
  HumanTarget,
  HumanTargetEntry,
  HumanPart,
  HumanPartEntry,
  HumanProxy,
  HumanProxyEntry,
} from './human-pack.js'
export {
  fitProxy,
  drawnPositions,
  proxyWeights,
  nearestWeights,
  coveredTriangles,
  underCover,
  inflate,
  proxyDelta,
  coveredBy,
  smoothUnder,
} from './proxy-fit.js'
export type { ProxyBinding, VertexWeights } from './proxy-fit.js'
export { humanProxy, proxyMaterial, coverBody } from './human-proxy.js'
export { cutGarment, mirrorOutline, skirtGarment } from './garment-cut.js'
export { outlineField3d } from './outline-field.js'
export { bindGarment, closestOnTriangle } from './garment-bind.js'
export {
  writeGarmentGlb,
  writeGarmentObj,
  readGarment,
  garmentRigidity,
  referenceName,
  metaFromName,
  unwrapGarment,
} from './garment-file.js'
export { unwrapUvs, carryUnwrapped } from './uv-unwrap.js'
export {
  writeCharacter,
  readCharacter,
  CHARACTER_FORMAT,
  CHARACTER_VERSION,
} from './character-file.js'
export type {
  Character,
  CharacterDoc,
  CharacterBody,
  SlotLook,
  ReadCharacter,
} from './character-file.js'
export {
  characterStore,
  splitBlobs,
  joinBlobs,
  CHARACTER_DB,
  WORKING_CHARACTER,
  nameCharacter,
} from './character-store.js'
export type { CharacterStore, CharacterName } from './character-store.js'
export type { Unwrapped, UnwrapOptions } from './uv-unwrap.js'
export {
  rigidGroups,
  similarityFit,
  applyRigid,
  rigidWeights,
  rigidDelta,
} from './proxy-rigid.js'
export type { RigidParts, RigidSpec, MeshPart } from './proxy-rigid.js'
export type {
  GarmentMesh,
  GarmentMeta,
  GarmentFileContent,
  GarmentMaterial,
  GarmentImage,
} from './garment-file.js'
export type { OutlineField, OutlineField3dOptions } from './outline-field.js'
export type { GarmentCut, GarmentCutOptions, ViewPoint } from './garment-cut.js'
export { clothShading, clothRecipe, FABRICS } from './cloth-shading.js'
export {
  faceDeltas,
  humanExpressions,
  blinker,
  lidsFollow,
  lipSync,
  babble,
  VISEMES,
  UNITS as FACE_POSE_UNITS,
  FACE_UNITS,
} from './human-expression.js'
export type { HumanExpressions, FaceLayer } from './human-expression.js'
export { studioEnvironment } from './environment.js'
export type { StudioEnvironmentOptions } from './environment.js'
export type { ClothShading, ClothLook, Fabric } from './cloth-shading.js'
export {
  DEFAULT_HUMAN_BODY,
  macroWeights,
  morphHuman,
  addHumanTarget,
  humanJoint,
  humanHeight,
  genderCorners,
  ageCorners,
  ageYears,
  ancestryCorners,
} from './human-morph.js'
export type { MacroBody } from './human-morph.js'
export { humanBody, skinMaterial } from './character-builder.js'
export { lipTint, stubbleTint, stubbleMask } from './skin-paint.js'
export { recolourCloth, clothSheenColour, textureMean } from './cloth-bake.js'
export { skinShading, skinThinness, SkinShadingPlugin } from './skin-shading.js'
export type { SkinShadingOptions } from './skin-shading.js'
export {
  MONK_TONES,
  skinLinear,
  skinColor,
  skinAlbedo,
  lipAlbedo,
  IRIS_TONES,
  irisColor,
  hairColor,
  hairDye,
} from './skin-tone.js'
export {
  loadHairLibrary,
  headScalp,
  hairMaterial,
  humanHair,
  placeHair,
  QUATERNIUS_HEADS,
} from './human-hair.js'
export { hairShading, HairShadingPlugin, HAIR_COLOURS } from './hair-shading.js'
export type { HairShading } from './hair-shading.js'
export type {
  HairLibrary,
  HairPart,
  HeadScalp,
  HairMaterial,
  HairPlacement,
  ReferenceHead,
} from './human-hair.js'
export {
  fitToScalp,
  applyFit,
  conformOutside,
  vertexNormals,
  headRegion,
  shellWarp,
  applyWarp,
  skinGaps,
  keepHeight,
  headRegionIndices,
} from './hair-fit.js'
export type { AxisFit, HeadLandmarks, ShellWarp } from './hair-fit.js'
export {
  humanFace,
  bodyEyes,
  eyeGaze,
  eyeTexture,
  eyeMaterial,
  lashMaterial,
  HM08_EYE_UV,
} from './human-face.js'
export type { HumanFace, EyeTexture, EyeTextureStyle } from './human-face.js'
export {
  EYE_STYLES,
  EYE_VERSION,
  ONE_EYE_LAYOUT,
  eyePngUrl,
  eyeSvgUrl,
  eyeStyle,
  eyeAsset,
  recolourIris,
  recolourEyePng,
  gltfEyeUvs,
} from './eye-art.js'
export type { EyeStyle, EyeLayout } from './eye-art.js'
export type { Rgb as SkinRgb } from './skin-tone.js'
export {
  humanRig,
  humanSkin,
  stiffenHead,
  armsOffTorso,
  humanBodyMaker,
  HM08_UAL,
} from './human-rig.js'
export type {
  HumanRigRow,
  HumanSkin,
  HumanRiggedBody,
  HumanBodyMaker,
} from './human-rig.js'
export type { HumanBody, SkinMaterial } from './character-builder.js'
export type { HeatBone, SkinWeights } from './bone-heat.js'
export {
  springChain,
  stepSpringChain,
  resetSpringChain,
  shiftSpringChain,
  SPRING_DEFAULTS,
} from './spring-chain.js'
export type {
  SpringChain,
  SpringCollider,
  SpringParams,
  Orientation as SpringOrientation,
} from './spring-chain.js'
export { springBones, springNodes } from './spring-chain-babylon.js'
export type { SpringBones, SpringBoneCollider } from './spring-chain-babylon.js'
export { realNow, wallMs, wallDate } from './clock.js'
export {
  makeCharacter,
  loadCharacterAssets,
  DEFAULT_CHARACTER,
  CHARACTER_SLOTS,
  CHARACTER_SOURCES,
} from './character.js'
export {
  hm08PackUrl,
  HM08_PACK_PATH,
  HM08_PACK_VERSION,
} from './character-settings.js'
export { coverSkin, BODY_WRAPPING_SLOTS } from './proxy-cover.js'
export type { SkinCoverOptions } from './proxy-cover.js'
export type { SurfaceMaterial } from './rigged-body.js'
export type {
  SceneCharacter,
  CharacterAssets,
  CharacterOptions,
  CharacterSource,
  CharacterGarment,
  ShownBody,
} from './character.js'
